import { AlertTriangle, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from './ui/sidebar'

export type AppNavRoute =
  | '/production'
  | '/jobs'
  | '/catalog'
  | '/asset-hub'
  | '/surroundings'
  | '/site'
  | '/site/portfolio'
  | '/site/screening'
  | '/site/sources'
  | '/building-review'
  | '/admin/machines'
  | '/admin/operations'
  | '/admin/code-analysis'
  | '/admin/tests'
  | '/admin/users'
  | '/admin/organizations'
  | '/admin/versions'
  | '/admin/jobs'
  | '/admin/external-api-keys'
  | '/admin/scheduler-settings'
  | '/admin/product-analytics'

export type AppNavItem = Readonly<{
  label: string
  to: AppNavRoute
  icon: LucideIcon
  exact?: boolean
  testId?: string
}>

export function NavMain({
  label,
  items,
  pathname,
  alerts,
  customItems,
}: {
  label: string
  items: readonly AppNavItem[]
  pathname: string
  alerts?: Readonly<Partial<Record<AppNavRoute, string>>>
  customItems?: Readonly<Partial<Record<AppNavRoute, ReactNode>>>
}) {
  const { setOpenMobile } = useSidebar()

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="font-mono text-[0.62rem] uppercase tracking-[0.12em]">
        {label}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            if (customItems?.[item.to]) return customItems[item.to]
            const Icon = item.icon
            const alert = alerts?.[item.to]
            return (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton
                  render={
                    <Link
                      to={item.to}
                      data-testid={item.testId}
                      onClick={() => setOpenMobile(false)}
                    />
                  }
                  isActive={isNavItemActive(pathname, item)}
                  tooltip={alert ? `${item.label}: ${alert}` : item.label}
                >
                  <Icon />
                  <span>{item.label}</span>
                  {alert && (
                    <AlertTriangle
                      aria-label={alert}
                      className="ml-auto text-warning group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:-right-0.5 group-data-[collapsible=icon]:-top-0.5 group-data-[collapsible=icon]:size-3!"
                    />
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

export function isNavItemActive(pathname: string, item: AppNavItem) {
  return item.exact ? pathname === item.to : pathname.startsWith(item.to)
}
